import React, { useState } from "react";
import { Upload, message, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { RcFile, UploadFile } from "antd/es/upload/interface";

interface UploadImageProps {
  maxFiles: number;
  accept?: string;
  maxFileSize?: number; // 单位：MB
  onUploaded: (urls: string[]) => void;
  action: (file: RcFile) => Promise<string>;
}

const UploadImage: React.FC<UploadImageProps> = ({
  maxFiles,
  accept,
  maxFileSize = 5, // 默认限制文件大小为 5MB
  onUploaded,
  action,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");

  const handleUpload = async (options: any) => {
    const { file } = options;
    try {
      const url = await action(file);
      message.success(`${file.name} uploaded successfully`);
      setUploadedUrls((prev) => {
        const newUrls = [...prev, url];
        setTimeout(() => {
          onUploaded(newUrls);
        }, 0);
        return newUrls;
      });
      setFileList((prev) =>
        prev.map((item) =>
          item.uid === file.uid
            ? { ...item, status: "done", url }
            : item
        )
      );
    } catch (error) {
      message.error(`${file.name} upload failed`);
      setFileList((prev) =>
        prev.map((item) =>
          item.uid === file.uid ? { ...item, status: "error" } : item
        )
      );
    }
  };

  const handleChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const handleRemove = async (file: UploadFile) => {
    // 更新自身组件的状态
    setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    setUploadedUrls((prev) => {
      const newUrls = prev.filter((_, index) => fileList[index]?.uid !== file.uid);
  
      // 将 `onUploaded` 放到异步回调中，避免直接在渲染过程中调用父组件状态更新
      setTimeout(() => {
        onUploaded(newUrls);
      }, 0);
  
      return newUrls;
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (file.url) {
      setPreviewImage(file.url);
    } else {
      message.error("Preview not available for this file");
      return;
    }
    setPreviewOpen(true);
  };

  const beforeUpload = (file: RcFile) => {
    // 检查文件大小是否超过限制
    if (file.size / 1024 / 1024 > maxFileSize) {
      message.error(`File size exceeds ${maxFileSize}MB limit`);
      return Upload.LIST_IGNORE; // 阻止文件上传
    }
    // 检查是否已达到最大文件数量
    if (fileList.length >= maxFiles) {
      message.error(`You can only upload up to ${maxFiles} pictures`);
      return Upload.LIST_IGNORE; // 阻止文件上传
    }
    return true;
  };

  return (
    <>
      <Upload
        customRequest={handleUpload}
        fileList={fileList}
        onChange={handleChange}
        onRemove={handleRemove}
        onPreview={handlePreview}
        listType="picture-card"
        maxCount={maxFiles}
        accept={accept}
        beforeUpload={beforeUpload} // 添加文件大小和数量限制
      >
        {fileList.length >= maxFiles ? null : (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        )}
      </Upload>
      <Modal
        open={previewOpen}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
      >
        <img alt="preview" style={{ width: "100%" }} src={previewImage} />
      </Modal>
    </>
  );
};

export default UploadImage;